/**
 * 刊登任务成功后，把 TEMU 已刊登行写入店铺「发布记录」（与手动发品 createPublishRecord 同源）。
 */

import { temuPlatformAPI } from '@/utils/api';
import {
  enrichTemuLocalPublishRecord,
  findTemuLocalPublishRecordBySpu,
} from '@/temu-platform/utils/temuLocalPublishRecordStore';

function parseListingTextRoot(listingText) {
  const raw = String(listingText || '').trim();
  if (!raw.startsWith('{')) return null;
  try {
    const root = JSON.parse(raw);
    return root && typeof root === 'object' ? root : null;
  } catch {
    return null;
  }
}

function extractPublishForm(listingText) {
  const root = parseListingTextRoot(listingText);
  if (!root) return null;
  const form = root.temuPublishForm || root.publishForm || null;
  return form && typeof form === 'object' ? form : null;
}

function extractTitleFromListingText(listingText) {
  const root = parseListingTextRoot(listingText);
  if (!root) return '';
  const previewTitle = String(root?.ipeJobPreview?.title || root?.ipeJobPreview?.productName || '').trim();
  if (previewTitle) return previewTitle;
  const formTitle = String(root?.temuPublishForm?.title || root?.temuPublishForm?.productName || '').trim();
  if (formTitle) return formTitle;
  const payload = root.temuPublishPayload || root.publishPayload || root.payload || null;
  return String(payload?.productName || payload?.title || '').trim();
}

/** 优先用后端回写的 addGoods 全量响应（含 productSkuId）。 */
function extractPlatformResFromListingText(listingText) {
  const root = parseListingTextRoot(listingText);
  if (!root) return null;
  const raw = root.temuPublishPlatformRes || root.platformRes || root.publishPlatformRes || null;
  return raw && typeof raw === 'object' ? raw : null;
}

function buildPlatformResFromLine(line) {
  const productId = String(line?.platformProductId || line?.platformSpu || '').trim();
  const skcIds = Array.isArray(line?.platformSkcIds)
    ? line.platformSkcIds
    : (Array.isArray(line?.platform_skc_ids) ? line.platform_skc_ids : []);
  const productSkcList = (Array.isArray(skcIds) ? skcIds : [])
    .map((id) => String(id || '').trim())
    .filter(Boolean)
    .map((productSkcId) => ({ productSkcId }));
  if (!productId && !productSkcList.length) return null;
  return {
    result: {
      productId,
      productSkcList,
    },
    productId,
    productSkcList,
  };
}

function countPlatformSkuIds(platformRes) {
  if (!platformRes || typeof platformRes !== 'object') return 0;
  const raw = JSON.stringify(platformRes);
  const matches = raw.match(/"productSkuId"\s*:\s*\d+/g)
    || raw.match(/"productSkuIdList"\s*:\s*\[/g)
    || [];
  return matches.length;
}

function recordNeedsEnrich(record) {
  if (!record) return true;
  const form = record.formSnapshot;
  const thinForm = !(form && typeof form === 'object'
    && (Array.isArray(form.skcList) ? form.skcList.length > 0 : false));
  const skuIds = Array.isArray(record.productSkuIds) ? record.productSkuIds : [];
  const skcList = Array.isArray(record.skcItems) ? record.skcItems : [];
  const missingSkus = !skuIds.length || skcList.some((skc) => {
    const ids = Array.isArray(skc?.productSkuIds) ? skc.productSkuIds : [];
    return !ids.length;
  });
  return thinForm || missingSkus;
}

/**
 * 本地货品缓存兜底：按 productId 拉 SKU 明细，拼成可被 assignPlatformSkuIds 识别的结构。
 */
async function tryBuildPlatformResFromLocalGoods(shopId, productId, line) {
  const pid = String(productId || '').trim();
  if (!pid) return null;
  try {
    const res = await temuPlatformAPI.listProductSkus(shopId, pid);
    const items = Array.isArray(res?.items) ? res.items : [];
    if (!items.length) return null;
    const bySkc = new Map();
    items.forEach((row) => {
      const skcId = String(row?.platformSkcId || row?.productSkcId || row?.skcId || '').trim();
      const skuId = Number(row?.productSkuId || row?.platformSkuId || row?.skuId);
      if (!(Number.isSafeInteger(skuId) && skuId > 0)) return;
      const key = skcId || '_';
      if (!bySkc.has(key)) bySkc.set(key, { productSkcId: skcId || undefined, productSkuIdList: [] });
      const bucket = bySkc.get(key);
      if (!bucket.productSkuIdList.includes(skuId)) bucket.productSkuIdList.push(skuId);
    });
    const productSkcList = [...bySkc.values()].filter((s) => s.productSkuIdList.length);
    if (!productSkcList.length) return null;
    // 若行上有 SKC 但缓存没带 skcId，按行 SKC 顺序挂上
    const lineSkcIds = Array.isArray(line?.platformSkcIds) ? line.platformSkcIds : [];
    if (lineSkcIds.length && productSkcList.every((s) => !s.productSkcId)) {
      productSkcList.forEach((s, i) => {
        if (lineSkcIds[i] != null) s.productSkcId = String(lineSkcIds[i]);
      });
    }
    return {
      result: { productId: pid, productSkcList },
      productId: pid,
      productSkcList,
    };
  } catch {
    return null;
  }
}

function resolveFormForLine(line, listingText) {
  const form = { ...(extractPublishForm(listingText) || {}) };
  if (!String(form.title || form.productName || '').trim()) {
    const title = String(line?.title || '').trim() || extractTitleFromListingText(listingText);
    if (title) {
      form.title = title;
      form.productName = form.productName || title;
    }
  }
  return form;
}

async function resolvePlatformResForLine(shopId, line, listingText) {
  let platformRes = extractPlatformResFromListingText(listingText);
  if (countPlatformSkuIds(platformRes) > 0) return platformRes;
  const fallback = buildPlatformResFromLine(line);
  const productId = String(line?.platformProductId || line?.platformSpu || '').trim();
  const fromGoods = await tryBuildPlatformResFromLocalGoods(shopId, productId, line);
  if (fromGoods && countPlatformSkuIds(fromGoods) > 0) return fromGoods;
  return platformRes || fallback;
}

/**
 * @param {{ shopId?: string|number, platformCode?: string, lines?: object[] }} detail
 * @returns {Promise<{ created: number, skipped: number, enriched: number }>}
 */
export async function ensureTemuPublishRecordsFromListingJobDetail(detail) {
  const shopId = detail?.shopId;
  const platform = String(detail?.platformCode || '').trim().toUpperCase();
  if (!shopId || platform !== 'TEMU') {
    return { created: 0, skipped: 0, enriched: 0 };
  }
  const lines = Array.isArray(detail?.lines) ? detail.lines : [];
  let created = 0;
  let skipped = 0;
  let enriched = 0;
  for (const line of lines) {
    const status = String(line?.lineStatus || line?.line_status || '').toLowerCase();
    if (status !== 'listed') {
      skipped += 1;
      continue;
    }
    const publishedSpu = String(line?.platformProductId || line?.platformSpu || '').trim();
    if (!publishedSpu) {
      skipped += 1;
      continue;
    }
    const listingText = line?.listingText || line?.listing_text || '';
    // eslint-disable-next-line no-await-in-loop
    const existing = await findTemuLocalPublishRecordBySpu(shopId, publishedSpu);
    if (existing) {
      if (!recordNeedsEnrich(existing)) {
        skipped += 1;
        continue;
      }
      const form = resolveFormForLine(line, listingText);
      // eslint-disable-next-line no-await-in-loop
      const platformRes = await resolvePlatformResForLine(shopId, line, listingText);
      try {
        // eslint-disable-next-line no-await-in-loop
        await enrichTemuLocalPublishRecord(shopId, existing.id, { form, platformRes });
        enriched += 1;
      } catch {
        skipped += 1;
      }
      continue;
    }
    const form = resolveFormForLine(line, listingText);
    // eslint-disable-next-line no-await-in-loop
    const platformRes = await resolvePlatformResForLine(shopId, line, listingText);
    try {
      // eslint-disable-next-line no-await-in-loop
      await temuPlatformAPI.createPublishRecord(shopId, {
        form,
        platformRes,
        publishedSpu,
        draftId: null,
        action: 'publish',
        source: 'listing-job',
      });
      created += 1;
    } catch {
      skipped += 1;
    }
  }
  return { created, skipped, enriched };
}
